import React, { FC, useEffect, useState } from "react";
import { Drawer } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useLanguage } from "../LanguageContext";
import { useNavigate } from "react-router-dom";
import { useMediaQuery, useTheme } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Snackbar from "@mui/material/Snackbar";
import { userDrawerTranslations } from "../userDrawer/userDrawerTranslations";
import { Language } from "../../Types/types";
import SnackbarContent from "@mui/material/SnackbarContent";
import { GoPersonFill } from "react-icons/go";
import { jwtDecode } from "jwt-decode";

interface AdminDrawerProps {
  open: boolean;
  onClose: () => void;
}

interface TokenPayload {
  iat: number;
  exp: number;
  roles: string[];
  username: string;
  clientIdentifier: string;
}

const AdminDrawer: FC<AdminDrawerProps> = ({ open, onClose }) => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [username, setUsername] = useState("");
  const [isAdmin, setIsAdmin] = useState(false); // Add state to check admin
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const t = userDrawerTranslations[language as Language];

  useEffect(() => {
    if (open) {
      const storedUsername = localStorage.getItem("username");
      const token = localStorage.getItem("token");

      if (storedUsername) {
        setUsername(storedUsername);
      }

      if (token) {
        try {
          const decodedToken = jwtDecode<TokenPayload>(token);
          setIsAdmin(decodedToken.roles.includes("ROLE_ADMIN"));
        } catch (error) {
          console.error("Invalid token", error);
        }
      }
    }
  }, [open]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
    onClose();
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(username);
    setSnackbarOpen(true);
  };

  const navigateTo = (path: string) => {
    navigate(path);
    onClose();
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isSmallScreen ? "100%" : 384,
          minWidth: 320,
          backgroundColor: "#f0f0f0",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(88, 110, 223, .8)",
        },
      }}
    >
      <div className="bg-white dark:bg-darkSecondaryBackground h-screen">
        <div className="p-4 flex flex-col h-full">
          <div className="flex justify-end p-2 dark:text-white">
            <button onClick={onClose}>
              <CloseIcon />
            </button>
          </div>
          <div className="flex flex-col h-full">
            <div className="flex relative my-2">
              <input
                placeholder={t.usernamePlaceholder}
                className="w-full p-2 pl-8 relative rounded-md dark:shadow-none shadow bg-lightPurple dark:bg-darkSecondaryBackground dark:border dark:border-purple text-black dark:text-white"
                value={username}
                readOnly
              ></input>
              <div className="absolute left-1 translate-y-1/2 text-black dark:text-white">
                <GoPersonFill
                  style={{
                    fontSize: "1.4rem",
                    position: "relative",
                    top: "-2px",
                  }}
                />
              </div>
              <button
                onClick={handleCopy}
                className="absolute right-1 top-2 flex"
              >
                <ContentCopyIcon className="mb-1 ml-2 bg-lightPurple dark:bg-darkSecondaryBackground text-black dark:text-white" />
                <div className="w-[1px] h-full bg-black dark:bg-white absolute"></div>
              </button>
            </div>
            <div className="dark:text-white flex flex-col gap-4 *:underline *:p-2 *:rounded-md my-4 font-semibold *:cursor-pointer">
              <p onClick={() => navigateTo("/packages-list")}>{t.packages}</p>
              <p onClick={() => navigateTo("/categories-list")}>
                {t.categories}
              </p>
              <p onClick={() => navigateTo("/licenses-list")}>{t.licenses}</p>
            </div>
            <div className="flex flex-col justify-end mt-auto">
              {isAdmin && (
                <button
                  onClick={() => navigateTo("/")}
                  className="bg-lightPurple hover:opacity-70 text-black font-semibold mb-2 w-full rounded-md p-2"
                >
                  {t.userZoneButton}
                </button>
              )}
              <button
                onClick={handleLogout}
                className="bg-darkPurple hover:bg-purple text-white w-full p-2 rounded-md mt-auto"
              >
                {t.logoutButton}
              </button>
            </div>
          </div>
        </div>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <SnackbarContent
            message={t.usernameCopied}
            sx={{
              backgroundColor: "orange",
              color: "white",
            }}
          />
        </Snackbar>
      </div>
    </Drawer>
  );
};

export default AdminDrawer;
